// client/src/pages/Admin/ProjectManager/components/ProjectFilters.tsx
/**
 * PROJECT FILTERS COMPONENT
 * 
 * Advanced filter controls for projects.
 * 
 * @module ProjectManager/Components/ProjectFilters
 */

import React from 'react';
import { RotateCcw } from 'lucide-react';
import { ProjectStatus, ProjectCategory, ProjectImpact } from '../types/project.types';
import { ProjectStatusLabels, ProjectCategoryLabels, ProjectImpactLabels } from '../types/project.types';
import { FILTER_OPTIONS } from '../constants/project.constants';

interface ProjectFiltersProps {
    filters: {
        status: ProjectStatus | 'all';
        category: ProjectCategory | 'all';
        impact: ProjectImpact | 'all';
        featured: boolean | 'all';
        isOngoing: boolean | 'all';
    };
    filterOptions: {
        categories: ProjectCategory[];
        statuses: ProjectStatus[];
        impacts: ProjectImpact[];
    };
    onFilterChange: (key: any, value: any) => void;
    hasActiveFilters: boolean;
    onResetFilters: () => void;
}

export const ProjectFilters: React.FC<ProjectFiltersProps> = ({
    filters,
    filterOptions,
    onFilterChange,
    hasActiveFilters,
    onResetFilters
}) => {
    return (
        <div className="filters-panel">
            <div className="filters-header">
                <h3>Filter Projects</h3>
                {hasActiveFilters && (
                    <button className="reset-filters-btn" onClick={onResetFilters}>
                        <RotateCcw size={14} />
                        Reset Filters
                    </button>
                )}
            </div>

            <div className="filters-grid">
                <div className="filter-group">
                    <label htmlFor="filter-status">Status</label>
                    <select
                        id="filter-status"
                        value={filters.status}
                        onChange={(e) => onFilterChange('status', e.target.value)}
                    >
                        <option value="all">All Statuses</option>
                        {filterOptions.statuses.map((status) => (
                            <option key={status} value={status}>
                                {ProjectStatusLabels[status]}
                            </option>
                        ))}
                    </select>
                </div>

                <div className="filter-group">
                    <label htmlFor="filter-category">Category</label>
                    <select
                        id="filter-category"
                        value={filters.category}
                        onChange={(e) => onFilterChange('category', e.target.value)}
                    >
                        <option value="all">All Categories</option>
                        {filterOptions.categories.map((category) => (
                            <option key={category} value={category}>
                                {ProjectCategoryLabels[category]}
                            </option>
                        ))}
                    </select>
                </div>

                <div className="filter-group">
                    <label htmlFor="filter-impact">Impact Level</label>
                    <select
                        id="filter-impact"
                        value={filters.impact}
                        onChange={(e) => onFilterChange('impact', e.target.value)}
                    >
                        <option value="all">All Impact Levels</option>
                        {filterOptions.impacts.map((impact) => (
                            <option key={impact} value={impact}>
                                {ProjectImpactLabels[impact]}
                            </option>
                        ))}
                    </select>
                </div>

                <div className="filter-group">
                    <label htmlFor="filter-featured">Featured</label>
                    <select
                        id="filter-featured"
                        value={String(filters.featured)}
                        onChange={(e) => onFilterChange('featured', e.target.value === 'true')}
                    >
                        <option value="all">All Projects</option>
                        <option value="true">Featured Only</option>
                        <option value="false">Non-Featured Only</option>
                    </select>
                </div>

                <div className="filter-group">
                    <label htmlFor="filter-ongoing">Ongoing</label>
                    <select
                        id="filter-ongoing"
                        value={String(filters.isOngoing)}
                        onChange={(e) => onFilterChange('isOngoing', e.target.value === 'true')}
                    >
                        <option value="all">All Projects</option>
                        <option value="true">Ongoing Only</option>
                        <option value="false">Completed Only</option>
                    </select>
                </div>
            </div>
        </div>
    );
};